import { Fragment, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';

export type Column<T> = {
  key: string;
  header: ReactNode;
  /** Texto só para leitor de tela quando `header` é vazio (padrão: "Ações"). */
  srHeader?: string;
  /** `right` = classe `r` (números). */
  align?: 'left' | 'right';
  /** Esconde a coluna em telas pequenas (classe `hs` do pacote). */
  hideOnMobile?: boolean;
  width?: number | string;
  /** Classes extras da célula (ex.: `id`, `val`, `wrap`). */
  cellClassName?: string;
  cellStyle?: CSSProperties;
  render: (row: T, index: number) => ReactNode;
};

export type DataTableProps<T> = {
  columns: Column<T>[];
  rows: readonly T[];
  rowKey: (row: T, index: number) => string | number;
  /** Linhas clicáveis e focáveis (Enter/Espaço selecionam). */
  onRowClick?: (row: T, index: number) => void;
  isSelected?: (row: T, index: number) => boolean;
  /** Conteúdo quando não há linhas (ex.: `EmptyState`). */
  empty?: ReactNode;
  /** Rótulo acessível da tabela. */
  label?: string;
  /** Linha própria (ex.: `InstallmentRow`); o padrão é `DataRow` com as `columns`. */
  renderRow?: (row: T, index: number) => ReactNode;
};

const cx = (...c: (string | false | undefined)[]) => c.filter(Boolean).join(' ') || undefined;

export type DataRowProps<T> = {
  columns: Column<T>[];
  row: T;
  index?: number;
  onRowClick?: (row: T, index: number) => void;
  selected?: boolean;
};

/** Uma linha (`<tr>`) da `DataTable`, para componentes de linha de domínio. */
export function DataRow<T>({ columns, row, index = 0, onRowClick, selected }: DataRowProps<T>) {
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row, index);
    }
  };
  return (
    <tr
      className={onRowClick ? (selected ? 'sel' : '') : undefined}
      tabIndex={onRowClick ? 0 : undefined}
      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
      onKeyDown={onRowClick ? onKey : undefined}
    >
      {columns.map((c) => (
        <td key={c.key} className={cx(c.cellClassName, c.align === 'right' && 'r', c.hideOnMobile && 'hs')} style={c.cellStyle}>
          {c.render(row, index)}
        </td>
      ))}
    </tr>
  );
}

const SR_ONLY: CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

/** Tabela do pacote (`.tablewrap` > `table.table`), genérica e sem regra de domínio. */
export function DataTable<T>({ columns, rows, rowKey, onRowClick, isSelected, empty, label, renderRow }: DataTableProps<T>) {
  return (
    <div className="tablewrap">
      <table className="table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cx(c.align === 'right' && 'r', c.hideOnMobile && 'hs')} style={c.width !== undefined ? { width: c.width } : undefined}>
                {c.header === '' ? <span style={SR_ONLY}>{c.srHeader ?? 'Ações'}</span> : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty !== undefined ? (
            <tr><td colSpan={columns.length}>{empty}</td></tr>
          ) : (
            rows.map((row, i) =>
              renderRow ? (
                <Fragment key={rowKey(row, i)}>{renderRow(row, i)}</Fragment>
              ) : (
                <DataRow key={rowKey(row, i)} columns={columns} row={row} index={i} onRowClick={onRowClick} selected={isSelected?.(row, i)} />
              ),
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
